/**
 * Locations API Client
 *
 * Fetches locations from Strapi filtered by location type.
 * Also resolves which locations belong to factories for the header selector.
 */

import { getHttpClient } from '@ufc/core'
import type { LocationSummary } from '../../domain/types'
import { transformToLocationSummary } from '../../api/transformers'

/**
 * Fetch active factory & office locations for UI selectors (public, no auth).
 * Used by the portal header LocationSelector and visit booking forms.
 */
export async function fetchWorkingSites(): Promise<LocationSummary[]> {
  const http = getHttpClient()
  // Locale is added once per GET by HttpClient (setLocaleProvider); do not append ?locale= here
  const response = await http.get<any>('/api/working-sites')
  const items = response?.data?.data ?? response?.data ?? []

  const locations: LocationSummary[] = Array.isArray(items)
    ? items.map(transformToLocationSummary)
    : []

  return locations
}

/**
 * Fetch locations for the header selector with factory resolution.
 * Calls /api/working-sites (public, not tenant-scoped) then resolves
 * factoryDocumentId for each location via a batch factory lookup.
 */
export async function fetchHeaderLocations(): Promise<LocationSummary[]> {
  const http = getHttpClient()

  const locations = await fetchWorkingSites()

  // Resolve factoryDocumentId for each location
  const factoryResponse = await http.get<any>(
    '/api/factories?populate[location][fields][0]=documentId&pagination[pageSize]=200&fields[0]=documentId&fields[1]=name'
  )
  const factories = factoryResponse?.data?.data ?? factoryResponse?.data ?? []

  /**
   * A site can host more than one plant record — Yanbu carries the mill, the
   * bulk operation and the bagged operation. This map used to keep whichever
   * came back last, so choosing the site silently narrowed everything to one
   * arbitrary operation, and every list and KPI in the portal read zero with
   * nothing on screen to say why.
   *
   * Count them first, and only derive a factory when the answer is unambiguous.
   * Selecting a site that hosts several operations means "this site", not "this
   * one operation" — the right response is to stop narrowing, not to guess.
   */
  const factoriesAtLocation = new Map<string, Array<{ documentId: string; name?: string }>>()
  if (Array.isArray(factories)) {
    for (const factory of factories) {
      const locDocId = factory.location?.documentId
      if (!locDocId) continue
      const list = factoriesAtLocation.get(locDocId) ?? []
      list.push({ documentId: factory.documentId, name: factory.name })
      factoriesAtLocation.set(locDocId, list)
    }
  }

  const locationToFactory = new Map<string, string>()
  const locationToFactoryName = new Map<string, string>()
  for (const [locDocId, list] of factoriesAtLocation) {
    // A single name only makes sense when there is a single plant; with several
    // the header shows the site's own name rather than one of them.
    if (list.length !== 1) continue
    locationToFactory.set(locDocId, list[0].documentId)
    if (typeof list[0].name === 'string' && list[0].name.trim()) {
      locationToFactoryName.set(locDocId, list[0].name)
    }
  }

  return locations.map((loc) => ({
    ...loc,
    factoryDocumentId: locationToFactory.get(loc.documentId) ?? null,
    factoryDocumentIds: (factoriesAtLocation.get(loc.documentId) ?? []).map((f) => f.documentId),
    factoryName: locationToFactoryName.get(loc.documentId) ?? null,
  }))
}
